import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { UserCircle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'

/**
 * Alıcı hesabına göre sipariş özeti — anasayfa.
 *
 * Kullanıcılar birden fazla Amazon alıcı hesabı kullanıyor; bir siparişin durumu ancak
 * onu veren hesaptan güncellenebiliyor. Bu tablo "hangi hesapta kaç sipariş hangi
 * durumda" sorusuna tek bakışta cevap verir ve kullanıcıya hangi hesaba girip
 * güncelleme yapması gerektiğini gösterir.
 *
 * Veri /orders/buyer-accounts'tan gelir. Hesap bilgisi eklenti 5.7.1 ile toplanmaya
 * başladı; hiç bilinen hesap yoksa tablo GÖSTERİLMEZ (boş "bilinmiyor" satırı basmak
 * anlamsız). Eski siparişler sonraki güncellemelerde tamamlandıkça satırlar dolar.
 */

interface BuyerAccountRow {
  buyerEmail: string | null
  total: number
  pending: number
  inTransit: number
  delivered: number
  cancelled: number
  refunded: number
  returned: number
  other: number
}

interface BuyerAccountsResponse {
  totalOrders: number
  knownAccounts: number
  unknownOrders: number
  accounts: BuyerAccountRow[]
}

export const BuyerAccountsTable: React.FC = () => {
  const [data, setData] = useState<BuyerAccountsResponse | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/orders/buyer-accounts`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
        })
        if (!res.ok) return
        const json: BuyerAccountsResponse = await res.json()
        if (!cancelled) setData(json)
      } catch { /* özet isteğe bağlı; anasayfayı bozmasın */ }
    })()
    return () => { cancelled = true }
  }, [])

  // Henüz hiçbir hesap bilinmiyorsa (eski eklenti / veri gelmedi) tablo yok.
  if (!data || data.knownAccounts === 0) return null

  const known = data.accounts.filter(a => a.buyerEmail)
  const unknown = data.accounts.find(a => !a.buyerEmail)

  const cell = (n: number, tone: string) =>
    n > 0
      ? <span className={`font-semibold ${tone}`}>{n}</span>
      : <span className="text-gray-300">—</span>

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }} className="mb-8">
      <Card className="glass border-orange-200 shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <UserCircle className="w-5 h-5 text-amber-600" />
            <div>
              <CardTitle className="text-lg font-semibold text-gray-800">Alıcı Hesaplarına Göre Siparişler</CardTitle>
              <CardDescription className="text-xs">
                Bir siparişin durumu yalnız onu veren hesaptan güncellenir — güncelleme yaparken o hesaba giriş yapın.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-orange-100">
                  <th className="text-left py-2 pr-3 font-medium">Hesap</th>
                  <th className="text-right py-2 px-2 font-medium">Toplam</th>
                  <th className="text-right py-2 px-2 font-medium">Teslim</th>
                  <th className="text-right py-2 px-2 font-medium">Kargoda</th>
                  <th className="text-right py-2 px-2 font-medium">Beklemede</th>
                  <th className="text-right py-2 px-2 font-medium">İptal</th>
                  <th className="text-right py-2 pl-2 font-medium">İade</th>
                </tr>
              </thead>
              <tbody>
                {known.map(a => (
                  <tr key={a.buyerEmail!} className="border-b border-orange-50 last:border-0">
                    <td className="py-2 pr-3 text-gray-800 truncate max-w-[260px]" title={a.buyerEmail!}>
                      {a.buyerEmail}
                    </td>
                    <td className="text-right py-2 px-2 text-gray-800 font-semibold">{a.total}</td>
                    <td className="text-right py-2 px-2">{cell(a.delivered, 'text-green-600')}</td>
                    <td className="text-right py-2 px-2">{cell(a.inTransit, 'text-blue-600')}</td>
                    <td className="text-right py-2 px-2">{cell(a.pending, 'text-amber-600')}</td>
                    <td className="text-right py-2 px-2">{cell(a.cancelled, 'text-red-600')}</td>
                    <td className="text-right py-2 pl-2">{cell(a.refunded + a.returned, 'text-purple-600')}</td>
                  </tr>
                ))}
                {unknown && unknown.total > 0 && (
                  <tr className="text-gray-400 italic">
                    <td className="py-2 pr-3">Hesabı henüz okunmamış</td>
                    <td className="text-right py-2 px-2">{unknown.total}</td>
                    <td className="text-right py-2 px-2">{unknown.delivered || '—'}</td>
                    <td className="text-right py-2 px-2">{unknown.inTransit || '—'}</td>
                    <td className="text-right py-2 px-2">{unknown.pending || '—'}</td>
                    <td className="text-right py-2 px-2">{unknown.cancelled || '—'}</td>
                    <td className="text-right py-2 pl-2">{(unknown.refunded + unknown.returned) || '—'}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {unknown && unknown.total > 0 && (
            <p className="text-[11px] text-gray-400 mt-2">
              "Henüz okunmamış" satırı eski siparişlerdir; her güncelleme turunda hesabı okunanlar ilgili satıra taşınır.
            </p>
          )}
        </CardContent>
      </Card>
    </motion.div>
  )
}

export default BuyerAccountsTable
